import { FUNCTIONS_URL } from './supabase.js';
import {
  getConsentHistory,
  getReferrer,
  getVisitorKey,
} from './visitorSession.js';

/**
 * Cliente da busca por intenção.
 * Dev → /api/portfolio-intent (Vite proxy)
 * Prod → Edge Function portfolio-intent
 */

function resolveEndpoint() {
  if (import.meta.env.DEV) return '/api/portfolio-intent';
  if (!FUNCTIONS_URL) {
    throw new Error(
      '[portfolio-intent] Defina VITE_SUPABASE_URL para chamar a Edge Function',
    );
  }
  return `${FUNCTIONS_URL}/portfolio-intent`;
}

function authHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'X-Visitor-Key': getVisitorKey(),
  };
  if (import.meta.env.DEV) return headers;

  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (anon) {
    headers.Authorization = `Bearer ${anon}`;
    headers.apikey = anon;
  }
  return headers;
}

async function postIntent(body, signal) {
  const res = await fetch(resolveEndpoint(), {
    method: 'POST',
    headers: authHeaders(),
    signal,
    body: JSON.stringify({
      visitor_key: getVisitorKey(),
      consent_history: getConsentHistory(),
      referrer: getReferrer(),
      ...body,
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.details || data?.error || res.statusText;
    throw new Error(detail);
  }
  return data;
}

/**
 * @param {object} opts
 * @param {string} opts.query
 * @param {AbortSignal} [opts.signal]
 */
export async function searchPortfolioIntent({ query, signal } = {}) {
  const data = await postIntent({ action: 'search', query }, signal);
  return {
    board: data.board,
    isCached: Boolean(data.is_cached),
    source: data.source ?? null,
    outOfScope:
      Boolean(data.out_of_scope) ||
      data.source === 'out_of_scope' ||
      data.board?.narrative === 'out_of_scope' ||
      data.board?.board_title === 'YOU DIED',
    similarity: typeof data.similarity === 'number' ? data.similarity : null,
    queryId: data.query_id ?? null,
    model: data.model ?? null,
  };
}

export async function bootstrapVisitorSession({ signal } = {}) {
  return postIntent({ action: 'bootstrap' }, signal);
}

export async function updateVisitorConsent(consent, { signal } = {}) {
  return postIntent(
    { action: 'consent', consent_history: Boolean(consent) },
    signal,
  );
}

export async function fetchVisitorHistory({ signal } = {}) {
  return postIntent({ action: 'history' }, signal);
}

/**
 * Reabre board do histórico sem Gemini.
 * @param {string} queryId
 */
export async function replayVisitorQuery(queryId, { signal } = {}) {
  const data = await postIntent({ action: 'replay', query_id: queryId }, signal);
  return {
    board: data.board,
    isCached: true,
    source: data.source ?? 'cache_history',
    outOfScope:
      Boolean(data.out_of_scope) ||
      data.source === 'out_of_scope' ||
      data.board?.narrative === 'out_of_scope' ||
      data.board?.board_title === 'YOU DIED',
    similarity: 1,
    queryId: data.query_id ?? null,
    query: data.query ?? null,
  };
}
